import React, { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Skeleton } from '@mui/material';
import { useOrg } from '../contexts/OrgContext';
import { usePerspective } from '../contexts/PerspectiveContext';
import { normalizeUserType } from '../utils/userType';
import type { User } from '../types/user';

interface ProtectedRouteProps {
  children: ReactElement;
  requiredUserType?: string;
  requireOrg?: boolean;
  currentUser: User | null;
  isLoading?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredUserType,
  requireOrg = false,
  currentUser,
  isLoading = false
}) => {
  const { org, isHydrating, setOrg } = useOrg();
  const { isInSwitchedView, perspectiveContext } = usePerspective();
  const location = useLocation();
  const currentUserType = normalizeUserType(currentUser?.userType);
  const requiredType = normalizeUserType(requiredUserType);

  // Infer org from URL on first load and persist to context/localStorage
  React.useEffect(() => {
    if (!location.pathname.startsWith('/internal/')) return;
    const segments = location.pathname.split('/');
    const pathOrg = segments[2];
    if (!org && (pathOrg === 'bare' || pathOrg === 'epl')) {
      setOrg(pathOrg as 'bare' | 'epl');
    }
  }, [location.pathname, org, setOrg]);

  // Show a loading skeleton while session/org are restoring
  if (isLoading || isHydrating) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={64} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Check authentication first
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Check user type if required
  // Allow access if admin is in switched view and the route matches the switched perspective
  if (requiredUserType && currentUserType !== requiredType) {
    // Check if admin is in switched view and trying to access the matching perspective route
    if (isInSwitchedView && perspectiveContext) {
      const switchedUserType = normalizeUserType(perspectiveContext.currentPerspective);
      // Allow if the switched perspective matches the required user type
      if (switchedUserType === requiredType) {
        // Continue to render children - admin is viewing as this user type
      } else {
        return <Navigate to="/" replace />;
      }
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // Handle org mismatch for internal routes and infer org from URL
  if (requireOrg && location.pathname.startsWith('/internal/')) {
    const pathParts = location.pathname.split('/');
    const pathOrg = pathParts[2]; // /internal/[org]/...

    // If org not set but URL contains a valid org, allow it through
    if (!org && (pathOrg === 'bare' || pathOrg === 'epl')) {
      return children;
    }

    if (org && pathOrg && pathOrg !== org) {
      // Redirect to correct org dashboard if path doesn't match stored org
      return <Navigate to={`/internal/${org}/dashboard`} replace />;
    }
  }

  // Check org requirement for internal routes after URL org inference had a chance to pass.
  if (requireOrg && !org) {
    return <Navigate to="/org-select" replace />;
  }

  return children;
};

export default ProtectedRoute;
